import { PROCESS_TABS, type ProcessTab } from "@/pages/processo/process-tabs";
import type { NotificationItem } from "@/lib/types";

/** TUT-517 — onde o clique no aviso leva: a aba do processo que fala do assunto dele. */
export interface DestinoDaNotificacao {
  processId: string;
  aba: ProcessTab;
  /** O laudo do aviso, para a aba Laudos abrir nele. */
  laudo?: string;
}

const ABA_DA_ENTIDADE: Readonly<Record<string, ProcessTab>> = {
  process: "resumo",
  // TUT-568 — a aba Prazos virou a aba Agendamento.
  deadline: "agendamento",
  expertise: "pericias",
  expert_report: "laudos",
  document: "documentos",
  // TUT-530 (D35): o aviso da resposta do advogado abre a aba onde o texto dela é lido.
  counsel_dispatch: "documentos",
};

/**
 * O destino do aviso no processo, ou `null` quando ele não fala de processo
 * nenhum (feedback, usuário…) ou a entidade já sumiu — aí o aviso abre o
 * detalhe na central (`/notificacoes?id=`). A entidade sem aba própria
 * (tarefa, ordem de serviço, solicitação) abre o Resumo do processo.
 */
export function destinoDaNotificacao(item: NotificationItem): DestinoDaNotificacao | null {
  if (!item.processo) return null;
  const aba = (item.entityType && ABA_DA_ENTIDADE[item.entityType]) || "resumo";
  if (item.entityType === "expert_report" && item.entityId) {
    return { processId: item.processo.id, aba, laudo: item.entityId };
  }
  return { processId: item.processo.id, aba };
}

/**
 * TUT-545 — o aviso que fala de uma conversa do chat (a "Mensagem do portal",
 * `chat.portal_message`, com `entityType: "chat_conversation"`) abre a
 * conversa na Caixa Única: devolve o id dela na Caixa (`chat:<id>`, o `?c=` de
 * `/conversas`), ou `null` quando o aviso não é de conversa.
 */
export function conversaDaNotificacao(item: NotificationItem): string | null {
  if (item.entityType !== "chat_conversation" || !item.entityId) return null;
  return `chat:${item.entityId}`;
}

/**
 * TUT-586a — o aviso SEM processo que tem tela própria abre nela, e não no
 * detalhe da central (onde só haveria o texto de novo): o feedback na fila de
 * feedbacks, o pedido de proposta do site na fila de propostas.
 */
const TELA_DA_ENTIDADE = {
  feedback: { to: "/feedbacks", rotulo: "Abrir feedbacks" },
  proposal_request: { to: "/propostas", rotulo: "Abrir propostas" },
} as const;

/**
 * TUT-586a — a ação "Abrir" do aviso, uma só para o painel do sino e a página:
 * o processo na aba (e no laudo) do assunto, a conversa na Caixa, a tela da
 * entidade ou, sem nada disso, o detalhe na própria central.
 */
export type AberturaDoAviso =
  | ({ tipo: "processo"; rotulo: "Abrir processo"; nomeDaAba: string } & DestinoDaNotificacao)
  | { tipo: "conversa"; rotulo: "Abrir conversa"; c: string }
  | { tipo: "tela"; rotulo: string; to: (typeof TELA_DA_ENTIDADE)[keyof typeof TELA_DA_ENTIDADE]["to"] }
  | { tipo: "detalhe"; rotulo: null; id: string };

export function aberturaDoAviso(item: NotificationItem): AberturaDoAviso {
  const conversa = conversaDaNotificacao(item);
  if (conversa) return { tipo: "conversa", rotulo: "Abrir conversa", c: conversa };
  const destino = destinoDaNotificacao(item);
  if (destino) {
    const nomeDaAba = PROCESS_TABS.find((aba) => aba.id === destino.aba)?.label ?? destino.aba;
    return { tipo: "processo", rotulo: "Abrir processo", nomeDaAba, ...destino };
  }
  const tela = item.entityType ? TELA_DA_ENTIDADE[item.entityType as keyof typeof TELA_DA_ENTIDADE] : undefined;
  if (tela) return { tipo: "tela", ...tela };
  return { tipo: "detalhe", rotulo: null, id: item.id };
}
